import { useRef, useCallback } from "react";

/**
 * Hook for playing back PCM audio received from the translation bridge.
 * Accepts raw PCM bytes (from WebSocket binary messages).
 * AudioContext is lazily initialized on first play() call to satisfy
 * the browser's autoplay policy (requires user gesture).
 */
export function useAudioPlayer(sampleRate: number = 16000) {
    const audioContextRef = useRef<AudioContext | null>(null);
    const workletNodeRef = useRef<AudioWorkletNode | null>(null);
    const initPromiseRef = useRef<Promise<void> | null>(null);

    const ensureInit = useCallback(async () => {
        if (workletNodeRef.current) return;
        if (initPromiseRef.current) {
            await initPromiseRef.current;
            return;
        }
        initPromiseRef.current = (async () => {
            const audioContext = new AudioContext({ sampleRate });
            audioContextRef.current = audioContext;
            await audioContext.audioWorklet.addModule("/audio-playback-worklet.js");
            const workletNode = new AudioWorkletNode(audioContext, "audio-playback-worklet");
            workletNode.connect(audioContext.destination);
            workletNodeRef.current = workletNode;
        })();
        await initPromiseRef.current;
    }, [sampleRate]);

    // init is now a no-op for backward compat — real init happens lazily
    const init = useCallback(async () => {}, []);

    const play = useCallback(async (audioData: ArrayBuffer) => {
        await ensureInit();
        if (workletNodeRef.current) {
            console.log(`[Player] Playing ${audioData.byteLength} bytes`);
            const int16 = new Int16Array(audioData);
            workletNodeRef.current.port.postMessage(int16);
        }
    }, [ensureInit]);

    const stop = useCallback(() => {
        if (workletNodeRef.current) {
            workletNodeRef.current.port.postMessage(null);
        }
    }, []);

    const cleanup = useCallback(async () => {
        stop();
        if (audioContextRef.current) {
            await audioContextRef.current.close();
            audioContextRef.current = null;
        }
        workletNodeRef.current = null;
    }, [stop]);

    return { init, play, stop, cleanup };
}
